import { queryOptions } from "@tanstack/react-query";
import sanityClient from "../../../client";

// Data access for the public marketing blog slice. All Sanity CMS access lives
// here, exposed as queryOptions the component consumes through TanStack Query.
// NOTE: this file is duplicated near-verbatim in src/features/app/blog/api.ts
// (the authenticated app blog reads the same Sanity content). They are kept
// separate per the colocation rule; if a shared Sanity query module is wanted
// that is a deliberate cross-cutting decision.

// ─── Types ───

export interface BlogPostImageAsset {
  _id: string;
  url: string;
}

export interface BlogPostImage {
  asset: BlogPostImageAsset;
}

export interface BlogPostSlug {
  current: string;
}

// A post as shown in the list/search grid. The list query does not select the
// author, so `name` is optional here and the UI falls back to "ifuelsmart".
export interface BlogPostSummary {
  title: string;
  slug: BlogPostSlug;
  mainImage: BlogPostImage;
  name?: string;
}

// A fully rendered post: adds the block-content body and author details.
export interface BlogPostDetail extends BlogPostSummary {
  body: unknown;
  authorImage?: unknown;
}

// projectId/dataset the BlockContent renderer needs to resolve image refs, read
// once from the client. Exposed here so components never import the Sanity
// client directly.
const { projectId, dataset } = sanityClient.config();
export const sanityImageConfig = { projectId, dataset };

// GROQ projection shared by the list and search queries so their selected fields
// stay identical.
const POST_SUMMARY_PROJECTION = `{
  title,
  slug,
  mainImage{
    asset->{
      _id,
      url
    }
  }
}`;

// Build the GROQ filter for a free-text title search: every space-separated term
// must match the title. NOTE: terms are interpolated into the query string,
// preserved verbatim from the original component. This is an injection surface;
// switching to GROQ parameters is a tracked follow-up, out of scope for the
// architecture migration.
function buildSearchQuery(search: string): string {
  const titleFilter = search
    .split(" ")
    .map((term) => `title match "${term}"`)
    .join(" && ");
  return `*[ ${titleFilter} ] ${POST_SUMMARY_PROJECTION}`;
}

// ─── Queries ───

async function getBlogPosts(): Promise<BlogPostSummary[]> {
  return sanityClient.fetch(`*[_type == "post"] ${POST_SUMMARY_PROJECTION}`);
}

export const blogPostsQuery = () =>
  queryOptions({
    queryKey: ["blog", "posts"],
    queryFn: getBlogPosts,
  });

async function searchBlogPosts(search: string): Promise<BlogPostSummary[]> {
  return sanityClient.fetch(buildSearchQuery(search));
}

export const blogPostsSearchQuery = (search: string) =>
  queryOptions({
    queryKey: ["blog", "posts", "search", search],
    queryFn: () => searchBlogPosts(search),
  });

async function getBlogPostBySlug(slug: string): Promise<BlogPostDetail | undefined> {
  const posts = await sanityClient.fetch<BlogPostDetail[]>(
    `*[slug.current == $slug]{
      title,
      slug,
      mainImage{
        asset->{
          _id,
          url
        }
      },
      body,
      "name": author->name,
      "authorImage": author->image
    }`,
    { slug }
  );
  return posts[0];
}

export const blogPostQuery = (slug: string) =>
  queryOptions({
    queryKey: ["blog", "post", slug],
    queryFn: () => getBlogPostBySlug(slug),
  });
